import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import z from 'zod'
import { ErrorBox } from '@/components/commons/error-box'
import { AlertTriangleIcon } from '@/components/ui/icons'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Scrollbar } from '@/components/ui/scrollbar'
import { FinalReviewDetailHeader } from '@/features/luxe-submissions/components/final-review-detail-header'
import { PinnedEscalationNote } from '@/features/luxe-submissions/components/pinned-escalation-note'
import { ScoreFormGallery } from '@/features/luxe-submissions/components/score-form-gallery'
import { ScoreFormListingDetails } from '@/features/luxe-submissions/components/score-form-listing-details'
import { ScoreFormLoading } from '@/features/luxe-submissions/components/score-form-loading'
import { ScoreFormPanel } from '@/features/luxe-submissions/components/score-form-panel'
import { useGetFinalReviewListingDetails } from '@/features/luxe-submissions/queries/use-get-final-review-listing-details'
import { useLuxeFeatureFlag } from '@/features/luxe-submissions/queries/use-get-luxe-feature-flags'
import { ScoreFormData } from '@/features/luxe-submissions/types'
import {
	findEscalationEntry,
	findSecondReviewEntry,
	getReviewerName
} from '@/features/luxe-submissions/utils'
import { raise } from '@/helpers/utils'
import { useURLParsedParams } from '@/hooks/use-url-parsed-params'
import { useTranslation } from '@/translations/i18n'

function LuxeSubmissionsFinalReviewDetails() {
	const { t } = useTranslation()
	const urlParams = useURLParsedParams(
		z.object({
			id: z.string()
		})
	)

	const listingId = urlParams.success
		? String(urlParams.data.id)
		: raise('Invalid listing ID in URL parameters')

	const { data, isLoading, isError } = useGetFinalReviewListingDetails({
		listingId
	})

	const { isEnabled: isPinnedEscalationNoteEnabled } = useLuxeFeatureFlag(
		'luxe-vv2-scorecard-final-review-comments'
	)
	const methods = useForm<ScoreFormData>({
		resolver: zodResolver(ScoreFormData)
	})

	useEffect(() => {
		if (data) {
			const answersRecord = Object.fromEntries(
				data.answers
					.filter((a) => a.answer != null)
					.map((a) => [a.questionId, a.answer as string])
			)
			methods.reset(
				{
					answers: answersRecord
				},
				{ keepDirty: false }
			)
		}
	}, [data, methods])

	if (isLoading) {
		return <ScoreFormLoading />
	}
	if (!data || isError) {
		return (
			<ErrorBox
				icon={<AlertTriangleIcon />}
				title={t('errors:default')}
			/>
		)
	}
	const facilities = [
		{ keyName: 'guests', count: data.maxGuests },
		{ keyName: 'bedrooms', count: data.bedrooms },
		{ keyName: 'beds', count: data.beds },
		{ keyName: 'baths', count: data.bathrooms }
	].filter((item) => item.count != null) as {
		keyName: 'bedrooms' | 'beds' | 'baths' | 'guests'
		count: number
	}[]
	// escalationNote carries no author or date, so those come from the entry
	const escalationDetails = findEscalationEntry(data.finalReviewComments)
	const secondReviewDetails = findSecondReviewEntry(data.finalReviewComments)

	return (
		<div className='flex flex-1 flex-col overflow-hidden'>
			<FormProvider {...methods}>
				{/* Header */}
				<FinalReviewDetailHeader listingData={data} />

				{/* Main content */}
				<div className='grid grid-cols-[1.5fr_2fr] overflow-hidden'>
					<ScrollArea
						className='h-full'
						scrollbar={<Scrollbar />}
						style={{ overflow: 'hidden' }}
					>
						<div className='border-neutral-200 border-r'>
							<PinnedEscalationNote
								blockersFailedCount={data.blockersFailedCount}
								designScore={data.visualVettingScore}
								details={escalationDetails}
								note={
									(isPinnedEscalationNoteEnabled
										? data.escalationNote
										: null) ?? escalationDetails?.note
								}
								reviewerName={
									getReviewerName({
										firstName: data.vetterFirstName ?? null,
										lastName: data.vetterLastName ?? null
									}).fullName
								}
								secondReviewDetails={secondReviewDetails}
								secondReviewNote={
									isPinnedEscalationNoteEnabled
										? secondReviewDetails?.note
										: null
								}
							/>
							<ScoreFormPanel
								formDisabled
								questions={data.questions}
							/>
						</div>
					</ScrollArea>

					<div className='flex flex-col gap-8 overflow-auto px-6'>
						<ScoreFormGallery
							listingId={data.airbnbListingId ?? data.id}
							photos={data.images}
							repositoryUrl={data.photoRepositoryUrl}
						/>
						<ScoreFormListingDetails facilities={facilities} />
					</div>
				</div>
			</FormProvider>
		</div>
	)
}

export { LuxeSubmissionsFinalReviewDetails }
